import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useMetaSettings } from "@/features/connectors";
import { useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { ArrowLeft, Plus } from "@phosphor-icons/react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { BetaBadge } from "../beta-badge";
import { ChannelLogo } from "../channel-logo";
import { isMetaChannel, type ChannelKind } from "../channels";
import { MetaChannelSetup } from "../connect-meta";
import { ConnectorCreateForm } from "../connector-create-form";

/** What the picker offers, in the order it shows it. */
const CHANNELS: ChannelKind[] = [
  "phone",
  "web",
  "email",
  "whatsapp",
  "messenger",
  "instagram",
];

/**
 * "Ajouter un canal": one entry point for every way the agent can be reached.
 * A first window asks *which* channel — each with its own coloured logo, so it
 * is found at a glance — and a second one configures that channel: the generic
 * connector form for a phone line, a mailbox or a web chat, the Meta sign-in
 * for WhatsApp, Messenger and Instagram.
 */
export const AddChannelDialog = () => {
  const { t } = useTranslation("connectors");
  const { company: clientId = "" } = useParams<{ company: string }>();
  const { data: meta } = useMetaSettings(clientId);
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState<ChannelKind | null>(null);

  const close = () => {
    setOpen(false);
    setChannel(null);
  };

  // Meta's sign-in is only on where the platform has an app for it.
  const unavailable = (kind: ChannelKind) =>
    isMetaChannel(kind) && meta !== undefined && !meta.enabled;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? setOpen(true) : close())}
    >
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4" weight="bold" />
          {t("connectors.addChannel")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
        {channel === null ? (
          <>
            <DialogHeader>
              <DialogTitle>{t("picker.title")}</DialogTitle>
              <DialogDescription>{t("picker.description")}</DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 gap-2 py-2 sm:grid-cols-2">
              {CHANNELS.map((kind) => (
                <ChannelCard
                  key={kind}
                  kind={kind}
                  disabled={unavailable(kind)}
                  onSelect={() => setChannel(kind)}
                />
              ))}
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <button
                type="button"
                onClick={() => setChannel(null)}
                className="mb-1 flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-3 w-3" />
                {t("picker.back")}
              </button>
              <div className="flex items-center gap-3">
                <ChannelLogo kind={channel} />
                <div className="space-y-1">
                  <DialogTitle className="flex items-center gap-2">
                    {t(`picker.${channel}.label`)}
                    <BetaBadge kind={channel} />
                  </DialogTitle>
                  <DialogDescription>
                    {t(`picker.${channel}.hint`)}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {isMetaChannel(channel) ? (
              <MetaChannelSetup channel={channel} onConnected={close} />
            ) : (
              <ConnectorCreateForm channel={channel} onCreated={close} />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ChannelCard = ({
  kind,
  disabled,
  onSelect,
}: {
  kind: ChannelKind;
  disabled: boolean;
  onSelect: () => void;
}) => {
  const { t } = useTranslation("connectors");
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "flex items-center gap-3 rounded-lg border p-3 text-left transition-colors",
        disabled
          ? "cursor-not-allowed opacity-50"
          : "hover:border-foreground/30 hover:bg-muted",
      )}
    >
      <ChannelLogo kind={kind} />
      <span className="min-w-0">
        <span className="flex items-center gap-2 text-sm font-medium">
          {t(`picker.${kind}.label`)}
          <BetaBadge kind={kind} />
        </span>
        <span className="block text-xs text-muted-foreground">
          {disabled ? t("picker.unavailable") : t(`picker.${kind}.hint`)}
        </span>
      </span>
    </button>
  );
};
